import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import SidebarPanel from './SidebarPanel';

function ArticleSidebarContents({
  headings = [],
  activeHeadingId = '',
}) {
  const { t } = useTranslation();
  const listRef = useRef(null);

  // The box keeps its size and only the list inside it scrolls: while the
  // article is read, the list follows along and keeps the current section in
  // the middle of the box. Only the list moves (scrollTo on it, not
  // scrollIntoView, which would scroll the page as well).
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector('.toc-link.active');

    if (!list || !active || list.scrollHeight <= list.clientHeight) {
      return;
    }

    const listBox = list.getBoundingClientRect();
    const activeBox = active.getBoundingClientRect();
    const target = list.scrollTop
      + (activeBox.top - listBox.top)
      - (list.clientHeight - activeBox.height) / 2;

    list.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [activeHeadingId]);

  // Nothing to list: no box at all, rather than an empty one.
  if (headings.length === 0) {
    return null;
  }

  return (
    <SidebarPanel
      icon="fa-solid fa-list-ul"
      title={t('articleSidebar.pageContents')}
      modifierClass="contents-box"
      collapsible
    >
      <nav
        ref={listRef}
        className="toc-list"
        aria-label={t('articleSidebar.pageContentsAriaLabel')}
      >
        {headings.map((heading, index) => {
          const isActive =
            activeHeadingId === heading.id;

          return (
            <a
              key={`${heading.id}-${heading.level}-${index}`}
              href={`#${heading.id}`}
              className={[
                'toc-link',
                `toc-level-${heading.level}`,
                isActive ? 'active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-current={
                isActive
                  ? 'location'
                  : undefined
              }
            >
              <span className="toc-link-marker" />

              <span className="toc-link-text">
                {heading.text}
              </span>
            </a>
          );
        })}
      </nav>
    </SidebarPanel>
  );
}

export default ArticleSidebarContents;
